import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import toast from 'react-hot-toast';
import {
  User, Mail, Phone, MapPin, Music, Award, BookOpen,
  Save, ArrowLeft, Sparkles, CheckCircle, Clock, XCircle,
  Users, FileText, Trophy, ChevronRight
} from 'lucide-react';

const getGamificationLevel = (puntos = 0) => {
  if (puntos >= 500) return { level: 6, title: 'Guardián del Patrimonio', next: 1000, color: 'from-amber-400 to-yellow-600' };
  if (puntos >= 250) return { level: 5, title: 'Curador de la Memoria Sonora', next: 500, color: 'from-purple-400 to-pink-600' };
  if (puntos >= 100) return { level: 4, title: 'Investigador Musical', next: 250, color: 'from-indigo-400 to-purple-600' };
  if (puntos >= 50) return { level: 3, title: 'Colaborador Avanzado', next: 100, color: 'from-blue-400 to-indigo-600' };
  if (puntos >= 20) return { level: 2, title: 'Colaborador Activo', next: 50, color: 'from-emerald-400 to-teal-600' };
  if (puntos >= 1) return { level: 1, title: 'Colaborador Inicial', next: 20, color: 'from-teal-400 to-emerald-500' };
  return { level: 0, title: 'Principiante Musical', next: 10, color: 'from-slate-400 to-slate-600' };
};

const AlumnoProfilePage = () => {
  const navigate = useNavigate();
  const [alumno, setAlumno] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    instrumento: '',
    telefono: '',
    direccion: '',
    nombre_tutor: '',
    telefono_tutor: '',
    detalles_adicionales: '',
    vive_con_padres: false,
  });

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const [meRes, contribRes] = await Promise.all([
        api.getAlumnoMe(),
        api.getAlumnoContributions().catch(() => ({ data: [] })),
      ]);

      const data = meRes.data;
      setAlumno(data);
      setContributions(contribRes.data || []);

      setFormData({
        instrumento: data.instrumento || '',
        telefono: data.telefono || '',
        direccion: data.direccion || '',
        nombre_tutor: data.nombre_tutor || '',
        telefono_tutor: data.telefono_tutor || '',
        detalles_adicionales: data.detalles_adicionales || '',
        vive_con_padres: Boolean(data.vive_con_padres),
      });
    } catch (error) {
      console.error('Error al cargar el perfil del alumno:', error);
      toast.error('No se pudo cargar la información de tu perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const res = await api.updateAlumnoProfile(formData);
      setAlumno((prev) => ({ ...prev, ...res.data }));
      toast.success('Perfil actualizado correctamente');
    } catch (error) {
      console.error('Error al guardar perfil:', error);
      toast.error(error.response?.data?.error || 'Error al actualizar el perfil');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-300 text-lg">Cargando tu perfil...</p>
        </div>
      </div>
    );
  }

  if (!alumno) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-8 text-center">
          <p className="text-white text-xl font-bold mb-4">No se encontró información del estudiante</p>
          <button
            onClick={() => navigate('/alumnos/dashboard')}
            className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl"
          >
            Ir al Dashboard
          </button>
        </div>
      </div>
    );
  }

  const initials = `${alumno.nombre?.[0] || ''}${alumno.apellido?.[0] || ''}`.toUpperCase() || 'AL';
  const totalPuntos = alumno.totalPuntos || 0;
  const levelInfo = getGamificationLevel(totalPuntos);
  const catedrasInscritas = alumno.CatedraAlumno || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto space-y-8">

        {/* Botón superior de retorno */}
        <button
          onClick={() => navigate('/alumnos/dashboard')}
          className="inline-flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Volver a mi Dashboard</span>
        </button>

        {/* Tarjeta Hero de Perfil y Gamificación */}
        <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6 sm:p-8">
          <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 text-center sm:text-left">
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white text-3xl font-extrabold shadow-xl shadow-purple-900/40 border border-white/15">
                {initials}
              </div>
              <div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-semibold mb-2">
                  <Sparkles size={13} />
                  Nivel {levelInfo.level}: {levelInfo.title}
                </div>
                <h1 className="text-3xl font-bold text-white">
                  {alumno.nombre} {alumno.apellido}
                </h1>
                <p className="text-slate-400 flex items-center justify-center sm:justify-start gap-2 mt-1">
                  <Mail size={15} className="text-purple-400" />
                  {alumno.email}
                </p>
                {formData.instrumento && (
                  <p className="text-slate-300 flex items-center justify-center sm:justify-start gap-2 mt-1 text-sm">
                    <Music size={15} className="text-emerald-400" />
                    Instrumento: <strong>{formData.instrumento}</strong>
                  </p>
                )}
              </div>
            </div>

            {/* Tarjeta de Puntos y Acceso al Ranking */}
            <div className="bg-slate-800/60 rounded-2xl p-5 border border-slate-700/60 min-w-[240px] text-center">
              <div className="flex items-center justify-center gap-2 text-amber-400 font-bold text-3xl">
                <Trophy size={28} />
                <span>{totalPuntos} PTS</span>
              </div>
              <p className="text-xs text-slate-400 mt-1">Puntos acumulados en la plataforma</p>
              <Link
                to="/gamification"
                className="mt-3 inline-flex items-center justify-center gap-1.5 w-full px-4 py-2 rounded-xl bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 border border-purple-500/30 text-xs font-semibold transition-colors"
              >
                <Award size={14} />
                Ver Tabla de Ranking
              </Link>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Columna Izquierda: Formulario de Datos Personales */}
          <div className="lg:col-span-7 bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6 sm:p-8">
            <h2 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
              <User className="text-purple-400" size={22} />
              Editar Mis Datos Personales
            </h2>
            <p className="text-slate-400 text-sm mb-6">
              Mantén actualizada tu información de contacto e instrumento musical
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                    Nombre (Solo lectura)
                  </label>
                  <input
                    type="text"
                    value={alumno.nombre || ''}
                    disabled
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-800/40 border border-slate-700/50 text-slate-400 cursor-not-allowed text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                    Apellido (Solo lectura)
                  </label>
                  <input
                    type="text"
                    value={alumno.apellido || ''}
                    disabled
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-800/40 border border-slate-700/50 text-slate-400 cursor-not-allowed text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
                    Instrumento Principal
                  </label>
                  <div className="relative">
                    <Music size={16} className="absolute left-3.5 top-3 text-slate-400" />
                    <input
                      type="text"
                      name="instrumento"
                      value={formData.instrumento}
                      onChange={handleChange}
                      placeholder="Ej. Guitarra, Violín, Piano, Canto..."
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-800/80 border border-slate-600/60 focus:border-purple-500 focus:outline-none text-white text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
                    Teléfono / WhatsApp
                  </label>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3.5 top-3 text-slate-400" />
                    <input
                      type="text"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                      placeholder="Ej. 0981 123 456"
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-800/80 border border-slate-600/60 focus:border-purple-500 focus:outline-none text-white text-sm"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
                  Dirección / Ciudad
                </label>
                <div className="relative">
                  <MapPin size={16} className="absolute left-3.5 top-3 text-slate-400" />
                  <input
                    type="text"
                    name="direccion"
                    value={formData.direccion}
                    onChange={handleChange}
                    placeholder="Tu dirección o ciudad de residencia"
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-800/80 border border-slate-600/60 focus:border-purple-500 focus:outline-none text-white text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-800">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
                    Nombre del Tutor / Contacto (Opcional)
                  </label>
                  <div className="relative">
                    <Users size={16} className="absolute left-3.5 top-3 text-slate-400" />
                    <input
                      type="text"
                      name="nombre_tutor"
                      value={formData.nombre_tutor}
                      onChange={handleChange}
                      placeholder="Nombre del padre, madre o tutor"
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-800/80 border border-slate-600/60 focus:border-purple-500 focus:outline-none text-white text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
                    Teléfono del Tutor (Opcional)
                  </label>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3.5 top-3 text-slate-400" />
                    <input
                      type="text"
                      name="telefono_tutor"
                      value={formData.telefono_tutor}
                      onChange={handleChange}
                      placeholder="Teléfono de contacto alternativo"
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-800/80 border border-slate-600/60 focus:border-purple-500 focus:outline-none text-white text-sm"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5">
                  Biografía Musical / Detalles Adicionales
                </label>
                <textarea
                  name="detalles_adicionales"
                  rows={3}
                  value={formData.detalles_adicionales}
                  onChange={handleChange}
                  placeholder="Cuéntanos sobre tus intereses musicales, repertorio o experiencia..."
                  className="w-full p-3.5 rounded-xl bg-slate-800/80 border border-slate-600/60 focus:border-purple-500 focus:outline-none text-white text-sm"
                />
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 text-white font-semibold shadow-lg shadow-purple-900/30 transition-all"
                >
                  <Save size={18} />
                  {saving ? 'Guardando cambios...' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          </div>

          {/* Columna Derecha: Mis Cátedras y Mis Aportes al Timeline */}
          <div className="lg:col-span-5 space-y-6">
            {/* Cátedras Inscritas */}
            <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <BookOpen className="text-emerald-400" size={20} />
                Mis Cátedras ({catedrasInscritas.length})
              </h3>
              {catedrasInscritas.length === 0 ? (
                <p className="text-slate-400 text-sm">No estás inscrito en ninguna cátedra actualmente.</p>
              ) : (
                <div className="space-y-3">
                  {catedrasInscritas.map((ca) => {
                    const cat = ca.Catedra;
                    if (!cat) return null;
                    return (
                      <Link
                        key={cat.id}
                        to={`/alumno/catedra/${cat.id}`}
                        className="flex items-center justify-between p-3.5 rounded-xl bg-slate-800/50 hover:bg-slate-800 border border-slate-700/60 hover:border-emerald-500/40 transition-all group"
                      >
                        <div>
                          <p className="text-white font-semibold text-sm group-hover:text-emerald-300 transition-colors">
                            {cat.nombre}
                          </p>
                          <p className="text-xs text-slate-400">
                            Año {cat.anio} • {cat.institucion}
                          </p>
                        </div>
                        <ChevronRight size={18} className="text-slate-400 group-hover:text-emerald-400 group-hover:translate-x-0.5 transition-all" />
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Mis Contribuciones al Timeline */}
            <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <FileText className="text-purple-400" size={20} />
                  Mis Aportes al Timeline ({contributions.length})
                </h3>
                <Link
                  to="/contribute"
                  className="text-xs font-semibold text-purple-400 hover:text-purple-300"
                >
                  + Nuevo Aporte
                </Link>
              </div>

              {contributions.length === 0 ? (
                <div className="text-center py-6 bg-slate-800/30 rounded-xl border border-slate-700/40">
                  <p className="text-slate-300 text-sm font-medium">Aún no realizaste aportes al Timeline</p>
                  <p className="text-slate-500 text-xs mt-1">
                    Colabora agregando compositores o sugerencias para ganar puntos extra.
                  </p>
                </div>
              ) : (
                <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                  {contributions.map((item) => {
                    const statusBadge = {
                      PUBLISHED: { text: 'Publicado', icon: CheckCircle, cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
                      APPLIED: { text: 'Aplicado', icon: CheckCircle, cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
                      PENDING_REVIEW: { text: 'En Revisión', icon: Clock, cls: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
                      REJECTED: { text: 'Rechazado', icon: XCircle, cls: 'bg-red-500/20 text-red-300 border-red-500/30' },
                    }[item.status] || { text: item.status, icon: Clock, cls: 'bg-slate-700 text-slate-300 border-slate-600' };

                    const StatusIcon = statusBadge.icon;

                    return (
                      <div
                        key={`${item.type}-${item.id}`}
                        className="p-3.5 rounded-xl bg-slate-800/50 border border-slate-700/60 space-y-1.5"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold text-white">
                            {item.first_name} {item.last_name}
                          </span>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${statusBadge.cls}`}>
                            <StatusIcon size={12} />
                            {statusBadge.text}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400">
                          Tipo: {item.type === 'Composer' ? 'Nuevo Compositor' : 'Sugerencia de Edición'}
                        </p>
                        {item.rejection_reason && (
                          <p className="text-xs text-red-300 bg-red-950/40 p-2 rounded border border-red-500/20">
                            Motivo: {item.rejection_reason}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlumnoProfilePage;
